import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { usePortfolioBuilder } from "./PortfolioBuilderContext";
import FilterBuilderBrowser from "./FilterBuilderBrowser";
import WatchlistCreatorBrowser from "./WatchlistCreatorBrowser";
import type { FilterDraft } from "../hooks/useFilterBuilder";
import type {
  WatchlistCreatorMode,
  SavedWatchlist,
  EditableWatchlist,
  CreatorDraft,
} from "../hooks/useWatchlistCreator";
import { applyPrebuiltOverrides } from "../prebuiltWatchlists";
import { formatListName } from "@/shared/utils/formatListName";
import { savedViewColor } from "@/shared/utils/savedViewHighlight";
import type { Portfolio } from "@/types/Portfolio";
import type { TagsWatchlist } from "@/shared/api/pref-storage/tags-watchlists";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";

/**
 * The Watchlists page: the builder, with your lists and the Newsquawk library
 * under its two start cards.
 *
 * There used to be a second view here, a three-pane library browser behind a
 * "Browse library" button. Once both lists sat on the builder's own landing —
 * searchable, in the widget menus' rows, opening into the builder — it
 * answered nothing the landing didn't, and was a second way of showing the
 * same lists with a different interaction model.
 */
export default function PortfolioBuilderBrowser() {
  const { queriesQuery } = useHeadlineQueriesCRUD();
  const { watchlistsQuery } = useTagsWatchlistsCRUD();
  const {
    setPreviewWatchlist,
    setPreviewFilter,
    setPreviewTagWatchlist,
    isPreviewingWatchlist,
    isPreviewingFilter,
  } = usePortfolioBuilder();

  // Creator state
  const [creatorMode, setCreatorMode] = useState<WatchlistCreatorMode>("ticker");
  const [creatorDraft, setCreatorDraft] = useState<CreatorDraft | null>(null);
  const [filterDraft, setFilterDraft] = useState<FilterDraft | null>(null);
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);
  const [editingTagWatchlist, setEditingTagWatchlist] = useState<TagsWatchlist | null>(null);

  // Advanced filter state
  const [advancedFilterModalOpen, setAdvancedFilterModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<CategoryQuery | null>(null);

  // The concierge panel creates resources itself (one reviewed step); this is
  // only the power-user hand-off, where the filter's watchlists have already
  // been created and resolved to real ids.
  const handleOpenFilterInBuilder = (draft: FilterDraft) => {
    setFilterDraft(draft);
    setEditingFilter(null);
    setAdvancedFilterModalOpen(true);
  };

  // Deep link: /watchlists-filters?create=ticker|keyword|tag|advanced opens
  // the corresponding creator (used by widget filter panels' "create new"
  // links and the onboarding checklist). Param is consumed and removed.
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Deep link into a combined watchlist, e.g. /watchlists-filters?editFilter=12
   *
   * The filter panel can apply one combined watchlist but cannot build one, so
   * its explanation of that limit needs somewhere to send people. Landing on
   * the page and asking them to find the list again is the kind of half-answer
   * that makes a limit feel like a fault.
   *
   * Consumed once and stripped from the URL, so a refresh or a back-navigation
   * doesn't reopen an editor the user has since closed.
   */
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const wanted = searchParams.get("editFilter");
    if (!wanted) return;
    const filter = (queriesQuery.data || []).find(
      (q) => String(q.id) === wanted
    );
    // Wait for the queries to load rather than giving up on the first render.
    if (!filter) return;
    deepLinkHandled.current = true;
    setEditingFilter(filter);
    setAdvancedFilterModalOpen(true);
    searchParams.delete("editFilter");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, queriesQuery.data]);

  useEffect(() => {
    const create = searchParams.get("create");
    if (!create) return;
    if (create === "ticker" || create === "keyword" || create === "tag") {
      // The type in this link is legacy; the builder no longer asks for one.
      setCreatorMode(create);
      setEditingPortfolio(null);
      setEditingTagWatchlist(null);
    } else if (create === "advanced") {
      setEditingFilter(null);
      setAdvancedFilterModalOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consume the param once per change
  }, [searchParams]);

  /**
   * After a save, show the thing that was just made.
   *
   * Saving used to navigate to the library — answering a question the user
   * hadn't asked and hiding the one they had ("did that work?"). Staying on
   * the builder and previewing the saved list puts the answer in the headline
   * strip underneath, and the list itself at the top of "Recently created".
   *
   * The setPreview* calls toggle, so each is skipped when that list is already
   * showing — otherwise saving a list twice in a row turned its preview off.
   */
  const handleSaved = useCallback(
    (saved?: SavedWatchlist) => {
      setEditingPortfolio(null);
      setEditingTagWatchlist(null);
      setCreatorDraft(null);
      // Back to the default form, or the next "Build it myself" after a tag
      // watchlist opened as a tag picker.
      setCreatorMode("ticker");
      if (!saved) return;
      if (saved.kind === "watchlist") {
        if (!isPreviewingWatchlist(saved.id)) setPreviewWatchlist(saved.id);
      } else if (saved.kind === "combined") {
        if (!isPreviewingFilter(saved.id)) setPreviewFilter(saved.id);
      } else {
        // Always set: the tags may have changed even if the list hasn't.
        const tw = (watchlistsQuery.data ?? []).find((w) => w.id === saved.id);
        setPreviewTagWatchlist(saved.id, saved.tags ?? tw?.tags ?? []);
      }
    },
    [
      setPreviewWatchlist,
      setPreviewFilter,
      setPreviewTagWatchlist,
      isPreviewingWatchlist,
      isPreviewingFilter,
      watchlistsQuery.data,
    ]
  );

  /**
   * A library list opened in the builder that fits its kind.
   *
   * Opened as the list it is — its terms, and the colour and priority the
   * user currently sees it in — with nothing saved until they save. What that
   * save does depends on what they changed (see libraryChanges): colour or
   * priority alone are kept as their own override of the library list; a
   * name, terms or alerts make a new list of their own. The copy is the same
   * one the concierge's "Build on it" makes.
   */
  const handleBuildOn = useCallback((item: EditableWatchlist) => {
    setEditingPortfolio(null);
    setEditingTagWatchlist(null);
    if (item.kind === "watchlist") {
      // The rows carry the stored record; the user sees it through their own
      // colour and priority, if they have set any.
      const [p] = applyPrebuiltOverrides([item.portfolio]);
      setCreatorMode(p.tickers?.length ? "ticker" : "keyword");
      setCreatorDraft({
        name: formatListName(p.name),
        tickers: p.tickers,
        keywords: p.keywords ?? [],
        basedOn: formatListName(p.name),
        libraryId: p.id,
        color: p.color,
        priority: p.priority,
      });
    } else if (item.kind === "tag") {
      setCreatorMode("tag");
      setCreatorDraft({
        name: formatListName(item.watchlist.name),
        tags: item.watchlist.tags,
        basedOn: formatListName(item.watchlist.name),
      });
    } else {
      const q = item.query;
      setFilterDraft({
        name: formatListName(q.queryName),
        searchKeyword: q.searchKeyword || undefined,
        tags: q.categories,
        portfolioIds: q.portfolioIds ?? [],
        tagsWatchlistIds: q.tagsWatchlistIds ?? [],
        filterMode: q.filterMode,
        keywordOp: q.keywordOp,
        keywordWatchlistOp: q.keywordWatchlistOp,
        basedOn: formatListName(q.queryName),
        libraryId: q.id,
        // The rows carry the query with the user's own colour applied; with
        // none stored, it highlights in the colour derived from its id.
        color: savedViewColor(q),
      });
      setEditingFilter(null);
      setAdvancedFilterModalOpen(true);
    }
  }, []);

  const builderHeader = (
    <div className="flex items-center gap-3 border-b border-gray-700/50 bg-gray-800 px-4 py-2.5">
      <h2 className="flex-1 text-sm font-semibold text-gray-100">
        {editingPortfolio || editingTagWatchlist
          ? "Edit watchlist"
          : "Build a watchlist"}
      </h2>
    </div>
  );

  return (
    <>
      <WatchlistCreatorBrowser
        inline
        isOpen
        // Inline, the builder closes itself — through onSaved and
        // onCancelled — so there is nothing for this to do.
        onClose={() => {}}
        mode={creatorMode}
        editingPortfolio={editingPortfolio}
        editingTagWatchlist={editingTagWatchlist}
        draft={creatorDraft}
        headerSlot={builderHeader}
        onOpenInBuilder={handleOpenFilterInBuilder}
        onOpenWatchlist={(draft) => {
          setEditingPortfolio(null);
          setEditingTagWatchlist(null);
          setCreatorDraft(draft);
        }}
        onSaved={handleSaved}
        onCancelled={() => {
          setEditingPortfolio(null);
          setEditingTagWatchlist(null);
          setCreatorDraft(null);
          setCreatorMode("ticker");
        }}
        onBuildOn={handleBuildOn}
        onEditWatchlist={(item) => {
          // The lists hold all three kinds, so the click routes to the editor
          // that can actually open the record.
          if (item.kind === "watchlist") {
            setEditingTagWatchlist(null);
            setEditingPortfolio(item.portfolio);
            setCreatorMode(item.portfolio.tickers?.length ? "ticker" : "keyword");
          } else if (item.kind === "tag") {
            setEditingPortfolio(null);
            setEditingTagWatchlist(item.watchlist);
            setCreatorMode("tag");
          } else {
            // A combination carries operators the flat form cannot express,
            // so it opens in the filter builder instead.
            setEditingFilter(item.query);
            setAdvancedFilterModalOpen(true);
          }
        }}
      />

      <FilterBuilderBrowser
        isOpen={advancedFilterModalOpen}
        onClose={() => { setAdvancedFilterModalOpen(false); setEditingFilter(null); setFilterDraft(null); }}
        queryId={editingFilter?.id}
        draft={filterDraft}
        // Saving in here is a save like any other: the modal closes itself
        // through onClose, and this is what tells the page what was made.
        onSaved={handleSaved}
      />
    </>
  );
}
